import { IAnnotation } from "@bronte/viewer-api/src/main";
import { finder } from "@medv/finder";

import { MousePosition, SelectionRect } from "@bronte/viewer/src/data/ViewerConfig";
import {
  AnnotationMathmlPayload,
  MathMlPayloadContent,
  ScrollType,
  TouchCoordinates,
} from "./annotationTypes";

import { ANNOTATION_PROPS, TOUCH_EVENTS } from "./annotationConstants";
import { DEVICE_TYPE, DeviceType } from "../../constants";

//TODO: remove functions taht are not used for annotations

let touchStartCoordinates: TouchCoordinates;

const getSelectionRectComputedValues = (
  currWindow: Window,
  top: number,
  bottom: number,
  left: number,
  right: number,
): SelectionRect => {
  if (currWindow.top === currWindow.self) {
    return { top, bottom, left, right };
  } else {
    top += currWindow.frameElement?.getBoundingClientRect().top || 0;
    left += currWindow.frameElement?.getBoundingClientRect().left || 0;
    bottom += currWindow.frameElement?.getBoundingClientRect().top || 0;
    right += currWindow.frameElement?.getBoundingClientRect().left || 0;

    return getSelectionRectComputedValues(currWindow.parent, top, bottom, left, right);
  }
};

const getSelectionChangeComputedValues = (): any => {
  const selection = document.getSelection() as any;
  if (selection?.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    return {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
    };
  }
};

/* TODO: BRNT-26696 This hard code should be replaced and we should use element attribute like data-user-select="none" to check if content has to be ignored.*/
export const ignoringDynamicContent = (text: string): string => {
  const labelsToIgnore = ["\\nShow\\n", "\\nHide\\n"];
  const regex = new RegExp(labelsToIgnore.join("|"), "g");
  return text.replace(regex, "");
};

export const handleTouchStart = (event: TouchEvent): void => {
  console.log("handleTouchStart", event);
  touchStartCoordinates = event?.changedTouches[0] || {};
};

export const getMousePosition = (event: any, deviceType: DeviceType): MousePosition => {
  // Following is for iOS only.
  if (deviceType === DEVICE_TYPE.IOS && event.type === TOUCH_EVENTS.touchend) {
    const touchEndCoordinates = event?.changedTouches[0] || {};
    const touchEndY = touchEndCoordinates.screenY || 0;
    const touchEndX = touchEndCoordinates.screenX || 0;
    return {
      positionX: touchEndX,
      positionY: touchEndY,
    };
  }

  // Following is for Android only.
  if (deviceType === DEVICE_TYPE.ANDROID) {
    if (event.type === TOUCH_EVENTS.selectionchange) {
      const { top, left } = getSelectionChangeComputedValues();
      return {
        positionX: left,
        positionY: top,
      };
    }
    return {
      positionX: event.screenX,
      positionY: event.screenY,
    };
  }

  return {
    positionX: event.screenX,
    positionY: event.screenY,
  };
};

export const getSelectionRect = (event: any, deviceType: DeviceType): SelectionRect => {
  const touchStartY = touchStartCoordinates?.screenY || 0;
  const touchStartX = touchStartCoordinates?.screenX || 0;

  // Following is for iOS only.
  if (event.type === TOUCH_EVENTS.touchend) {
    const touchEndCoordinates = event?.changedTouches[0] || {};
    const touchEndY = touchEndCoordinates.screenY || 0;
    const touchEndX = touchEndCoordinates.screenX || 0;

    return {
      top: touchStartY <= touchEndY ? touchStartY : touchEndY,
      bottom: touchStartY >= touchEndY ? touchStartY : touchEndY,
      left: touchStartX <= touchEndX ? touchStartX : touchEndX,
      right: touchStartX >= touchEndX ? touchStartX : touchEndX,
    };
  }

  // Following is for Android only.
  if (deviceType === DEVICE_TYPE.ANDROID) {
    if (event.type === TOUCH_EVENTS.selectionchange) {
      const { top, right, bottom, left } = getSelectionChangeComputedValues();
      return {
        top: touchStartY <= top ? touchStartY : top,
        bottom: touchStartY >= bottom ? touchStartY : bottom,
        left: touchStartX <= left ? touchStartX : left,
        right: touchStartX >= right ? touchStartX : right,
      };
    }
    return {
      top: touchStartY <= event.screenY ? touchStartY : event.screenY,
      bottom: touchStartY >= event.screenY ? touchStartY : event.screenY,
      left: touchStartX <= event.screenX ? touchStartX : event.screenX,
      right: touchStartX >= event.screenX ? touchStartX : event.screenX,
    };
  }

  /**
   * In web we need to get selection bound and then travel towards parent iFrames to add their position.
   * This is needed to calculate exact position of the selection in nested iFrames.
   */
  const selectedBoundRect = window.getSelection()?.getRangeAt(0)?.getBoundingClientRect();
  const { top, bottom, left, right } = (selectedBoundRect as SelectionRect) || {};
  return getSelectionRectComputedValues(window, top, bottom, left, right);
};

// Method to get all the nodes with same annotation id.
// used for edit and delete scenarios
export const getTargetNodes = (annotation: IAnnotation): NodeListOf<Element> => {
  const selector = `[data-annotation-type][data-annotation-id="${annotation?.annotationId}"]`;
  return document.querySelectorAll(selector);
};

// method to check if current selection overlaps with already existing annotation
export const isSelectionOverlapping = (range: any): boolean => {
  if (range.collapsed || range.toString() == "") {
    return !!getAnnotatedNode(range.commonAncestorContainer);
  } else {
    return range.getNodes([3]).some((textNode: any) => !!getAnnotatedNode(textNode));
  }
};

// method to check if css class used for annotation/highlight already applied on given HTML node
export const isNodeAnnotated = (node: HTMLElement): boolean => {
  const hasBGApplierClass =
    node.nodeType == node.ELEMENT_NODE &&
    node.nodeName.toLowerCase() == ANNOTATION_PROPS.HIGHLIGHT_TAG_NAME &&
    node.classList.contains(ANNOTATION_PROPS.ANNOTATION_HIGHLIGHT_CLASS_NAME);
  return hasBGApplierClass;
};

// method to check if css class used for flashcard already applied on given HTML node
export const isNodeFlashCardAnnotation = (node: HTMLElement): boolean => {
  const hasFlashcardApplierClass =
    node.nodeType == node.ELEMENT_NODE &&
    node.nodeName.toLowerCase() == ANNOTATION_PROPS.HIGHLIGHT_TAG_NAME &&
    node.classList.contains(ANNOTATION_PROPS.ANNOTATION_FLASHCARD_CLASS_NAME);
  return hasFlashcardApplierClass;
};

/** method to find out if any of the nodes in ancestry has the css class used for annotation/highlight.
 * used in finding if current selection is overlapping with previous note or highlight. This excludes flashcard annotation.
 */
export const getAnnotatedNode = (node: HTMLElement): HTMLElement | undefined => {
  while (node) {
    if (isNodeAnnotated(node)) {
      return node;
    }
    node = node.parentNode as HTMLElement;
  }
  return;
};

// method to find out first non annotated node in ancestry without flashcard or highlight/node class added.
export const getFirstNonAnnotatedNode = (node: HTMLElement): HTMLElement | undefined => {
  while (node) {
    if (!(isNodeAnnotated(node) || isNodeFlashCardAnnotation(node))) {
      return node;
    }
    node = node.parentNode as HTMLElement;
  }
  return;
};

export const getCssClassForColor = (color = ""): string => color.toLowerCase() || "";

// Check if annotation is note
export const isNote = (annotation: IAnnotation): boolean => !!annotation?.noteText;

// Check if annotation is flash card
export const isFlashCard = (annotation: IAnnotation): boolean => {
  return (
    annotation?.annotationType?.toLowerCase() ===
    ANNOTATION_PROPS.ANNOTATION_TYPE_FLASHCARD.toLowerCase()
  );
};

// Get handler class name
export const getHandlerClassName = (isFlashCard: boolean): string => {
  return isFlashCard
    ? ANNOTATION_PROPS.ANNOTATION_FLASHCARD_ICON_HANDLER_CLASS_NAME
    : ANNOTATION_PROPS.ANNOTATION_NOTE_ICON_HANDLER_CLASS_NAME;
};

export const scrollIntoView = (element: Element, isComponent = false): void => {
  if (isComponent) {
    /**
     * [TODO] Fix this: If element is in child iFrame like MMI, MCQ then scrollIntoView won't work in iOS and Safari.
     */
    element.scrollIntoView();
    return;
  }

  /**
   * Using window.scrollTo instead of element.scrollIntoView
   * because scrollIntoView silently fails when user is also scrolling at the same time.
   */
  const scrollMargin = 50;
  window.scrollTo({
    top: element.getBoundingClientRect().top + window.scrollY - scrollMargin,
  });
};

// Scroll into the element with offset
export const isAnnotationApplied = (annotation: IAnnotation): boolean => {
  const selector = `[data-annotation-type][data-annotation-id="${annotation.annotationId}"]`;
  return !!document.querySelector(selector);
};

// check if event is triggered within current selection
export const isInBoundingClientRect = (event: MouseEvent): boolean | undefined => {
  const cX = event.clientX;
  const cY = event.clientY;
  const selection = window.getSelection();
  if (selection?.rangeCount === 0) {
    return false;
  }
  // using window selection below as getBoundingClientRect is available only in native selection API
  const rect = selection?.getRangeAt(0).getBoundingClientRect();
  return rect && cX > rect.left && cX < rect.right && cY > rect.top && cY < rect.bottom;
};

export const isEqualNumber = (value1: number, value2: number, buffer: number): boolean => {
  return value1 >= value2 - buffer && value1 <= value2 + buffer;
};

export const hasMathML = (selectedText: string): boolean => {
  return selectedText.indexOf("</math>") > -1;
};

// create and return the math ml payload to be send with annotation request data
export const getMathMLPayload = (selection: any): AnnotationMathmlPayload => {
  const mathMlReqData = {
    hasMathml: true,
    content: getMathMlContentArray(selection?.toString()),
  };
  return mathMlReqData;
};

export const getElementHeight = (htmlNode: HTMLElement) => htmlNode.style?.height;
export const isHidden = (htmlNode: HTMLElement) => {
  return htmlNode.style?.display === "none" || htmlNode.clientHeight === 0;
};

const getTextFromMathML = (selectedText: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = selectedText;
  return (tempDiv.textContent || tempDiv.innerText || "").trim();
};

// create content array from selection when selection contains mathml
const getMathMlContentArray = (selectedText: string): MathMlPayloadContent[] => {
  const contentArray: Array<MathMlPayloadContent> = [];
  const selectedTextArr = selectedText?.split(/(<math(?:\s+[^>]*)?>[\s\S]*?<\/math>)/);
  for (let i = 0; i < selectedTextArr.length; i++) {
    let selectedTextArrVal = selectedTextArr[i] || "";
    let content: MathMlPayloadContent;
    if (selectedTextArrVal.includes("<math")) {
      content = {
        type: "mathml",
        schema: "https://schema.pearson.com/cite/schema/mathML/1.0",
        content: {
          text: selectedTextArrVal,
        },
      };
    } else {
      const mathMLText = getTextFromMathML(selectedTextArr[i + 1]);
      if (mathMLText) {
        selectedTextArrVal = selectedTextArrVal.replace(mathMLText, "");
      }
      content = {
        type: "text",
        content: {
          text: selectedTextArrVal,
        },
      };
    }
    contentArray.push(content);
  }
  return contentArray;
};

export const snapOffsetToWordStart = (text: string, offset: number): number => {
  const words = text.split(" ");
  let currentOffset = 0;
  for (let i = 0; i < words.length; i++) {
    const wordLength = words[i].length;
    if (offset <= currentOffset + wordLength) {
      return currentOffset;
    }
    currentOffset += wordLength + 1; // +1 for space
  }
  return words.length;
};

export const getSelectionOffset = (node: HTMLElement, offset: number): number => {
  return snapOffsetToWordStart(node.textContent ?? "", offset);
};

function resolveRangeStartTextPosition(
  range: Range,
): { textNode: Node; offsetInTextNode: number } | null {
  const { startContainer, startOffset } = range;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    return { textNode: startContainer, offsetInTextNode: startOffset };
  }

  if (startContainer.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const el = startContainer as HTMLElement;
  const atOrAfter = el.childNodes[startOffset] ?? el.childNodes[startOffset - 1];
  if (!atOrAfter) {
    return null;
  }

  if (atOrAfter.nodeType === Node.TEXT_NODE) {
    const offsetInTextNode =
      atOrAfter === el.childNodes[startOffset] ? 0 : (atOrAfter.textContent ?? "").length;
    return { textNode: atOrAfter, offsetInTextNode };
  }

  const walker = document.createTreeWalker(atOrAfter, NodeFilter.SHOW_TEXT);
  const firstText = walker.nextNode();
  if (!firstText) {
    return null;
  }

  return {
    textNode: firstText,
    offsetInTextNode:
      atOrAfter === el.childNodes[startOffset] ? 0 : (firstText.textContent ?? "").length,
  };
}

/**
 * TTS seek offset under a [data-id] element from the live selection range.
 * Uses document order / textContent (including annotation highlights), not annotation
 * bookmark indexes that skip [data-annotation-id] text.
 */
export function getTtsOffsetFromRange(dataElement: HTMLElement, range: Range): number {
  const start = resolveRangeStartTextPosition(range);
  if (!start) {
    return 0;
  }

  return getTtsOffsetFromTextPosition(dataElement, start.textNode, start.offsetInTextNode);
}

/** Same walk as getOffsetFromBeginning, but snaps with the real in-node offset (not bookmark). */
function getTtsOffsetFromTextPosition(
  parent: HTMLElement,
  textNode: Node,
  offsetInTextNode: number,
): number {
  const childNodes = parent.childNodes;

  if (childNodes.length === 0) {
    return getSelectionOffset(parent, offsetInTextNode);
  }

  let offset = 0;
  for (let i = 0; i < childNodes.length; i++) {
    const child = childNodes[i] as Node;
    if (child instanceof HTMLElement && child.classList.contains("popover-wrapper")) {
      continue;
    }

    const textContent = child.textContent ?? "";

    const containsTarget =
      child === textNode ||
      (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).contains(textNode));

    if (containsTarget) {
      if (child.nodeType === Node.TEXT_NODE) {
        return offset + snapOffsetToWordStart(child.textContent ?? "", offsetInTextNode);
      }

      const el = child as HTMLElement;
      if (el.childNodes.length) {
        return offset + getTtsOffsetFromTextPosition(el, textNode, offsetInTextNode);
      }

      if (hasMathML(textContent)) {
        return offset;
      }

      return offset + getSelectionOffset(el, offsetInTextNode);
    }

    if (child instanceof HTMLElement && hasMathML(textContent)) {
      const mathMlText = child.querySelector("[aria-label]")?.getAttribute("aria-label") ?? "";
      offset += mathMlText.length;
    } else {
      offset += textContent.length;
    }
  }

  return offset;
}

export function getOffsetFromBeginning(
  parent: HTMLElement,
  textNode: Node,
  bookmarkOffset: number,
): number {
  const childNodes = parent.childNodes;

  if (childNodes.length === 0) {
    return getSelectionOffset(parent, bookmarkOffset);
  }

  let offset = 0;
  for (let i = 0; i < childNodes.length; i++) {
    const child = childNodes[i] as HTMLElement;
    // In case of the key-term and footnotes we need to ignore the content inside the popover.
    const nodesToBeSkipped = child?.classList?.contains("popover-wrapper");
    if (nodesToBeSkipped) {
      continue;
    }

    const textContent = child.textContent ?? "";

    if (child.contains(textNode)) {
      if (child.childNodes.length) {
        const childOffset = getOffsetFromBeginning(child, textNode, bookmarkOffset);
        return offset + childOffset;
      }
      /**
       * if child contains the text node and text content is
       * math ml then we assume it is starting from the beginning of math
       * and return the offset immediately
       * */
      if (hasMathML(textContent)) {
        return offset;
      } else {
        /**
         * Calculate the offset from the child.
         * If user starts the selection in between the words
         * we will return the offset from the beginning of the words
         * it will make sure the highlighting and read along are in sync
         */
        const bookmarkTextOffset = getSelectionOffset(child, bookmarkOffset);
        return offset + bookmarkTextOffset;
      }
    } else if (hasMathML(textContent)) {
      // if the text content is math ml then we need to take the length of aria-label instead of actual math ml text content
      const mathMlText = child.querySelector("[aria-label]")?.getAttribute("aria-label") ?? "";
      offset += mathMlText.length;
    } else {
      // if text node is normal text then we can directly take the length of the text
      offset += textContent.length;
    }
  }

  return offset;
}

const isElementVisible = (element: any) => {
  if (!element) return false;

  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    style.opacity !== "0" &&
    element.offsetHeight > 0 &&
    element.offsetWidth > 0
  );
};

export const scrollToWithPolling = (config: {
  maxRetries: number;
  retryInterval: number;
  type: ScrollType;
  isComponent: boolean;
  annotation?: IAnnotation;
  dataId?: string;
}): void => {
  let retries = 0;
  const { maxRetries, retryInterval, isComponent, type, annotation, dataId } = config;

  if (type === "annotation" && !annotation) {
    console.log("[ScrollToWithPolling] Annotation data is required for type 'annotation'.");
    return;
  }

  if (type === "element" && !dataId) {
    console.log("[ScrollToWithPolling] DataId is required for type 'element'.");
    return;
  }

  // console.log(
  //   `[ScrollToWithPolling] Starting polling for ${
  //     type === "annotation" ? "annotation" : "element"
  //   }`,
  // );

  const pollForElement = (): void => {
    retries++;

    // console.log(
    //   `[ScrollToWithPolling] Attempt ${retries}/${maxRetries} for ${
    //     type === "annotation" ? `annotationId "${annotation?.annotationId}"` : `dataId "${dataId}"`
    //   }`,
    // );

    let element: Element | null = null;

    if (type === "annotation") {
      element =
        document.querySelector(
          `[data-annotation-type][data-annotation-id="${annotation?.annotationId}"]`,
        ) || document.querySelector(annotation?.bookmark?.commonAncestorCssSelector || "");
      // Notes don't navigate to the exact location if the target element is hidden (e.g., inside collapsed sections).
      // So navigate to the annotation icon instead.
      if (element && !isElementVisible(element)) {
        element = document.querySelector(
          `[data-icon-type][data-annotation-id="${annotation?.annotationId}"]`,
        );
      }
    } else if (type === "element") {
      element = document.querySelector(`[data-id="${dataId}"]`) || document.getElementById(dataId!);
    }

    if (element) {
      console.log(`[ScrollToWithPolling] Element found. Scrolling into view.`);
      scrollIntoView(element, isComponent);
      return;
    }

    if (retries < maxRetries) {
      setTimeout(pollForElement, retryInterval);
    } else {
      if (retries === maxRetries - 1) {
        console.warn(`[ScrollToWithPolling] Element not found after ${maxRetries} attempts.`);
      }
    }
  };

  pollForElement();
};

// subscribe and forget if possible, otherwise subscribe to the event
// do not use subscribeAndForget directly!
// since some components migth not have new CDP version
export const trySubscribeAndForget = (event: any, callback: any) => {
  if (event && typeof event.subscribeAndForget === "function") {
    event.subscribeAndForget(callback);
  } else if (event) {
    event.subscribe(callback);
  }
};

// method to find out first non annotated node in ancestry without flashcard or highlight/node class added.
const userAgent = window.navigator.userAgent;

export const Browser = {
  isiOS:
    /iPhone|iPad/i.test(userAgent) || (/Macintosh/.test(userAgent) && "ontouchend" in document),
  isAndroid: /Android/i.test(userAgent),
  isFirefox: /firefox/i.test(window.navigator.userAgent),
};

export const addArialLabelToAnnotationNodes = (responseData: IAnnotation): void => {
  const annotationNodes = getTargetNodes(responseData) || [];
  annotationNodes.forEach((node) => {
    const color = responseData.color || "";
    const textContent = node.textContent || "";
    if (textContent.trim().length) {
      node.setAttribute(
        "aria-label",
        `${color} highlighted text ${textContent} End of ${color} highlighted text`,
      );
    }
  });
};

export const isUserSelectNone = (node: SVGSVGElement): boolean => {
  if (typeof node === "undefined") return false;
  const parent = node.parentNode;

  if (!(parent instanceof HTMLElement)) return false;
  const cssObj = window.getComputedStyle(parent, null);
  const userSelect =
    cssObj?.getPropertyValue("user-select") || cssObj?.getPropertyValue("-webkit-user-select");
  return userSelect === "none";
};

const annotationHighlightSelector = [
  `.${ANNOTATION_PROPS.ANNOTATION_HIGHLIGHT_CLASS_NAME}`,
  `.${ANNOTATION_PROPS.ANNOTATION_FLASHCARD_CLASS_NAME}`,
  `.${ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME}`,
].join(",");

/**
 * A highlight belongs to the screen it was created on. Matching text in another
 * chapter is not the same highlight, even when the DOM path is identical.
 */
export const annotationMatchesScreen = (
  annotation: { elementId?: string; screenId?: string },
  currentScreenId?: string,
): boolean => {
  if (!currentScreenId) return true;
  if (annotation.elementId && annotation.elementId !== currentScreenId) return false;
  if (annotation.screenId && annotation.screenId !== currentScreenId) return false;
  return true;
};

/** Drop highlight wrappers left in the DOM after a chapter change. */
export const clearRenderedAnnotationHighlights = (root: ParentNode): void => {
  const nodes = Array.from(root.querySelectorAll(annotationHighlightSelector));
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const node = nodes[i];
    const parent = node.parentNode;
    if (!parent) continue;
    while (node.firstChild) {
      parent.insertBefore(node.firstChild, node);
    }
    parent.removeChild(node);
  }
  if ("normalize" in root && typeof root.normalize === "function") {
    root.normalize();
  }
};

export const escapeAttributeValue = (value: string): string => {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
};

export const computeCssSelector = (nodeElement: Element): string => {
  try {
    // Prefer data-id so the bookmark points at the content block, not a
    // structural path that is the same in every chapter with the same layout.
    return finder(nodeElement, {
      idName: () => false,
      className: () => false,
      attr: (name: string) => name === "data-id",
    });
  } catch (err) {
    return "";
  }
};
